import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Flag, X } from "lucide-react";

export default function AdminTavolini() {
    const [teams, setTeams] = useState([]);
    const [forfeits, setForfeits] = useState([]);
    const [teamId, setTeamId] = useState("");
    const [scope, setScope] = useState("from_date");
    const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);

    const load = async () => {
        try {
            const [t, f] = await Promise.all([
                api.get("/teams"),
                api.get("/tavolino-forfeits"),
            ]);
            const list = Array.isArray(t.data) ? t.data : (t.data.teams || []);
            setTeams([...list].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
            setForfeits(f.data || []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const apply = async () => {
        setError(null); setMsg(null);
        if (!teamId) { setError("Seleziona una squadra"); return; }
        const team = teams.find((t) => t.id === teamId);
        const scopeLabel = scope === "all" ? "da INIZIO torneo (sovrascrive anche le partite già giocate)" : `dal ${fromDate} in avanti`;
        if (!confirm(`Assegnare TAVOLINI 3-0 CONTRO ${team?.name} ${scopeLabel}?\nIn ogni sua partita (campionato + coppe) vince l'avversario. Punti sì, gol no.`)) return;
        setSaving(true);
        try {
            const body = { team_id: teamId, scope };
            if (scope === "from_date") body.from_date = fromDate;
            const { data } = await api.post("/matches/tavolino-forfeit/apply", body);
            setMsg(`Fatto: ${data.matches_affected} partite messe a tavolino contro ${team?.name}.`);
            setTeamId("");
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const cancel = async (f) => {
        if (!confirm(`Annullare i tavolini di ${f.team_name}? Le partite torneranno 'da giocare'.`)) return;
        setSaving(true); setError(null); setMsg(null);
        try {
            const { data } = await api.post("/matches/tavolino-forfeit/cancel", { team_id: f.team_id });
            setMsg(`Annullati: ${data.matches_reset} partite riportate a 'da giocare'.`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    if (loading) return <div className="op-label text-t8f95a3 animate-pulse p-6">// CARICAMENTO…</div>;

    return (
        <div className="max-w-3xl" data-testid="admin-tavolini">
            <div className="flex items-center gap-3 mb-2">
                <Flag className="w-6 h-6 text-tff7a1a" />
                <h1 className="op-heading text-tffffff text-2xl">TAVOLINI</h1>
            </div>
            <p className="op-mono text-xs text-t8f95a3 mb-6 leading-relaxed max-w-2xl">
                Assegna tavolini 3-0 <b>contro</b> una squadra: in ogni sua partita (campionato + coppe)
                vince l'avversario, che prende i <b>3 punti</b> in classifica ma <b>senza i gol</b>
                (il 3-0 non entra nella differenza reti). Per un tavolino su una singola partita usa
                invece il tabellino dal calendario.
            </p>

            <div className="op-card p-5 mb-6 space-y-4" data-testid="tavolini-form">
                <div>
                    <label className="op-label text-[10px] text-t8f95a3 block mb-1">SQUADRA (che perde a tavolino)</label>
                    <select
                        value={teamId}
                        onChange={(e) => setTeamId(e.target.value)}
                        className="w-full bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-2 focus:border-tff7a1a focus:outline-none"
                        data-testid="tavolini-team-select"
                    >
                        <option value="">-- seleziona squadra --</option>
                        {teams.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="op-label text-[10px] text-t8f95a3 block mb-2">PERIODO</label>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" checked={scope === "from_date"} onChange={() => setScope("from_date")} data-testid="tavolini-scope-from" />
                            <span className="text-sm text-tc9ccd3">Da una data in avanti fino a fine stagione</span>
                        </label>
                        {scope === "from_date" && (
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="ml-6 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-1.5 focus:border-tff7a1a focus:outline-none"
                                data-testid="tavolini-from-date"
                            />
                        )}
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="radio" checked={scope === "all"} onChange={() => setScope("all")} data-testid="tavolini-scope-all" />
                            <span className="text-sm text-tc9ccd3">Da inizio torneo <span className="text-tff4d4d">(sovrascrive anche le partite già giocate)</span></span>
                        </label>
                    </div>
                </div>

                <button onClick={apply} disabled={saving || !teamId} className="op-btn op-btn-primary text-sm w-full disabled:opacity-40" data-testid="tavolini-apply">
                    {saving ? "Applico…" : "Assegna tavolini"}
                </button>
            </div>

            {error && <div className="op-card p-3 mb-4 text-tff4d4d text-sm" data-testid="tavolini-error">{error}</div>}
            {msg && <div className="op-card p-3 mb-4 text-t7ce38b text-sm" data-testid="tavolini-msg">{msg}</div>}

            <div className="op-label text-[10px] text-t8f95a3 mb-2">TAVOLINI ATTIVI ({forfeits.length})</div>
            {forfeits.length === 0 ? (
                <div className="op-mono text-xs text-t5c6370">Nessun tavolino attivo.</div>
            ) : (
                <div className="space-y-2">
                    {forfeits.map((f) => (
                        <div key={f.team_id} className="op-card p-3 flex items-center justify-between gap-3" data-testid={`tavolini-active-${f.team_id}`}>
                            <div>
                                <span className="text-tffffff text-sm font-semibold">{f.team_name}</span>
                                <span className="op-mono text-[10px] text-t5c6370 ml-2">
                                    {f.scope === "all" ? "da inizio torneo" : `dal ${f.from_date}`}
                                </span>
                            </div>
                            <button onClick={() => cancel(f)} disabled={saving} className="op-btn op-btn-ghost text-xs text-tff4d4d flex items-center gap-1" data-testid={`tavolini-cancel-${f.team_id}`}>
                                <X className="w-3 h-3" /> Annulla
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
